/* eslint-disable max-lines */
/**
 * SlideSensePage
 *
 * Main page for the Slide Sense feature.  Three-panel layout:
 *   - Left  (250px): FolderNav sidebar
 *   - Center (flex): SlideSearchBar + SlideLibrary grid
 *   - Right (350px): SlideBuilder drop zone
 *
 * Top-level tabs switch between "Library" (the default view) and "Admin"
 * (only visible when the user has an admin role).
 *
 * DnD context wraps the whole page so slides can be dragged from the
 * library grid into the builder, and reordered within the builder.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  type Modifier,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { v4 as uuidv4 } from "uuid"

import type {
  BuilderSlide,
  RemovedSlide,
  SlideSenseCollection,
  SlideSenseDeck,
  SlideSenseFolder,
  SlideSenseSlide,
} from "../types"

import {
  AlertCircle,
  BookOpen,
  Calendar,
  Download,
  Layers,
  LayoutGrid,
  Loader2,
  Search,
  Settings,
  Sparkles,
  Trash2,
  User,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { SidebarInset } from "@/components/ui/sidebar"
import { Spinner } from "@/components/ui/spinner"
import { useViewportBelow } from "@/hooks/use-viewport-below"
import { downloadBlob } from "@/lib/download"

import { fetchAdminDecks, fetchAdminFolders, fetchSlideSenseAdminMe } from "../admin-api"
import { acknowledgeRemovals, deleteCollection, fetchCollection, mergeSlides } from "../api"
import { AdminFileTree } from "../components/AdminFileTree"
import { DeckBrowser } from "../components/DeckBrowser"
import { FolderBreadcrumbs } from "../components/FolderBreadcrumbs"
import { PresentationMode } from "../components/presentation/PresentationMode"
import { SearchScopeChips } from "../components/SearchScopeChips"
import { SlideBuilder } from "../components/SlideBuilder"
import { SlideHubLanding } from "../components/SlideHubLanding"
import { SlideLibrary } from "../components/SlideLibrary"
import { SlidePreviewModal } from "../components/SlidePreviewModal"
import { type SearchMode, SlideSearchBar } from "../components/SlideSearchBar"
import { SlideSenseHelp } from "../components/SlideSenseHelp"
import { SlideSenseUsageDashboard } from "../components/SlideSenseUsageDashboard"
import { SlideThumbnailImage } from "../components/SlideThumbnailImage"
import { useAiSearch } from "../hooks/use-ai-search"
import { useCollections } from "../hooks/use-collections"
import { useFolders } from "../hooks/use-folders"
import { usePresenterModes } from "../hooks/use-module-config"
import { useSlideSearch } from "../hooks/use-slide-search"
import { useSlideSenseNav } from "../hooks/use-slide-sense-nav"
import { useSlides } from "../hooks/use-slides"
import { type SlideAddSource, useSlideSenseTelemetry } from "../hooks/use-telemetry"
import { formatSlideSenseDate } from "../utils/date-format"
import { rotateDraftPreviewHintScopeId } from "../utils/preview-mode-hint"
import {
  buildSlideSenseViewParams,
  getSlideSenseViewParam,
  resolveSlideSenseView,
  type SlideSenseView,
} from "../utils/view-url"

// ---------------------------------------------------------------------------
// My Presentations panel sub-components (internal to this page)
// ---------------------------------------------------------------------------

function MyPresentationsList({
  collections,
  isLoading,
  onLoad,
  onDelete,
  onDownload,
  loadingCollectionId,
  downloadingCollectionId,
}: {
  collections: SlideSenseCollection[]
  isLoading: boolean
  onLoad: (collection: SlideSenseCollection) => void
  onDelete: (collection: SlideSenseCollection) => void
  onDownload: (collection: SlideSenseCollection) => void
  loadingCollectionId: string | null
  downloadingCollectionId: string | null
}) {
  const { t, i18n } = useTranslation()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  if (collections.length === 0) {
    return (
      <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-3">
        <BookOpen className="text-muted-foreground/40 h-10 w-10" />
        <p className="text-sm">{t("slideSense.noCollections")}</p>
        <p className="text-muted-foreground/70 text-xs">{t("slideSense.noCollectionsHint")}</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4">
      {collections.map((collection) => {
        const dateStr = formatSlideSenseDate(
          collection.updated_at || collection.created_at,
          i18n.language
        )
        const isLoadingThis = loadingCollectionId === collection.id
        const isDownloadingThis = downloadingCollectionId === collection.id
        const firstSlideId = collection.slide_ids[0]

        const handleLoad = () => onLoad(collection)
        const stopAndRun = (fn: () => void) => (e: React.MouseEvent) => {
          e.stopPropagation()
          fn()
        }

        return (
          <div
            key={collection.id}
            className="group border-border/45 bg-background/10 hover:border-border/70 hover:bg-background/18 relative flex cursor-pointer flex-col overflow-hidden rounded-lg border shadow-sm transition-[border-color,background-color,box-shadow] duration-200 hover:shadow-md"
            role="presentation"
            // Mouse convenience only: keyboard users reach the real Load button in the
            // action overlay (revealed by group-focus-within).
            onClick={handleLoad}
          >
            {/* Cover image — first slide thumbnail */}
            <div className="bg-muted/40 relative aspect-[16/9] w-full overflow-hidden">
              <SlideThumbnailImage
                fallbackIcon={<BookOpen aria-hidden className="h-6 w-6" />}
                slideId={firstSlideId}
              />

              {/* Slide count badge — top-right */}
              <div className="absolute top-2 right-2">
                <Badge className="gap-1 text-[10px]" variant="secondary">
                  {t("slideSense.slideCount", { count: collection.slide_count })}
                </Badge>
              </div>

              {/* Action overlay — visible on hover or keyboard focus-within. */}
              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity duration-200 group-focus-within:opacity-100 group-hover:opacity-100">
                <Button
                  aria-label={`${t("slideSense.load")}: ${collection.name}`}
                  className="h-9 gap-1.5 px-3"
                  disabled={isLoadingThis}
                  size="sm"
                  title={t("slideSense.load")}
                  variant="secondary"
                  onClick={stopAndRun(handleLoad)}
                >
                  {isLoadingThis ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <BookOpen className="h-4 w-4" />
                  )}
                  <span>{t("slideSense.load")}</span>
                </Button>
                <Button
                  aria-label={t("slideSense.download")}
                  className="h-9 w-9 p-0"
                  disabled={isDownloadingThis}
                  size="sm"
                  title={t("slideSense.download")}
                  variant="secondary"
                  onClick={stopAndRun(() => onDownload(collection))}
                >
                  {isDownloadingThis ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  aria-label={t("slideSense.delete")}
                  className="text-destructive hover:bg-destructive hover:text-destructive-foreground h-9 w-9 p-0"
                  size="sm"
                  title={t("slideSense.delete")}
                  variant="secondary"
                  onClick={stopAndRun(() => onDelete(collection))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Card body */}
            <div className="flex flex-1 flex-col gap-1 p-2.5">
              <p
                className="text-foreground line-clamp-2 text-sm leading-snug font-medium"
                title={collection.name}
              >
                {collection.name}
              </p>
              {collection.owner_name && (
                <div className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
                  <User className="h-3 w-3 shrink-0" />
                  <span className="truncate">{collection.owner_name}</span>
                </div>
              )}
              <div className="text-muted-foreground flex items-center gap-1.5 text-[11px]">
                <Calendar className="h-3 w-3 shrink-0" />
                <span>{dateStr}</span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function SlideSensePage() {
  const { t } = useTranslation()
  // ---- Shared nav state (driven by sidebar) --------------------------------
  const nav = useSlideSenseNav()
  // Stable setter refs for use inside effect/callback dependency lists.
  // `nav` itself is the whole store object — its reference changes after
  // every store update, so depending on it directly causes infinite loops.
  const setNavCollectionCount = useSlideSenseNav((s) => s.setCollectionCount)
  const setNavIsAdmin = useSlideSenseNav((s) => s.setIsAdmin)
  const navSelectAllSlides = useSlideSenseNav((s) => s.selectAllSlides)
  const navSelectMyPresentations = useSlideSenseNav((s) => s.selectMyPresentations)
  const navSelectAdmin = useSlideSenseNav((s) => s.selectAdmin)
  const navSelectUsage = useSlideSenseNav((s) => s.selectUsage)
  const navSelectHelp = useSlideSenseNav((s) => s.selectHelp)
  const telemetry = useSlideSenseTelemetry()
  const selectedFolderId = nav.selectedFolderId
  const showMyPresentations = nav.showMyPresentations
  const activeTab = nav.activeTab

  // Top-level "view" state synced to URL (?view=all|my|admin|usage). Folder
  // selection stays in-memory (URL param would explode if folders are renamed
  // or deleted). Drives bookmarkability and back/forward navigation between
  // the four primary views.
  const [searchParams, setSearchParams] = useSearchParams()
  const viewParam = searchParams.get("view")
  const validViewParam = getSlideSenseViewParam(viewParam)
  const urlViewSyncRef = useRef<SlideSenseView | null>(null)

  const navSelectHome = useSlideSenseNav((s) => s.selectHome)

  // URL → state on mount and whenever the URL view param changes
  useEffect(() => {
    if (!validViewParam) return

    urlViewSyncRef.current = validViewParam
    if (validViewParam === "home") navSelectHome()
    else if (validViewParam === "my") navSelectMyPresentations()
    else if (validViewParam === "admin") navSelectAdmin()
    else if (validViewParam === "usage") navSelectUsage()
    else if (validViewParam === "help") navSelectHelp()
    else navSelectAllSlides()
  }, [
    validViewParam,
    navSelectHome,
    navSelectMyPresentations,
    navSelectAdmin,
    navSelectUsage,
    navSelectHelp,
    navSelectAllSlides,
  ])

  // State → URL when nav changes (skip when a specific folder is selected)
  useEffect(() => {
    if (selectedFolderId) return
    const desired = resolveSlideSenseView(activeTab, showMyPresentations)
    const pendingUrlView = urlViewSyncRef.current

    if (pendingUrlView === desired) {
      urlViewSyncRef.current = null
    }

    const next = buildSlideSenseViewParams(searchParams, desired, pendingUrlView)
    if (next) {
      setSearchParams(next, { replace: true })
    }
  }, [activeTab, showMyPresentations, selectedFolderId, searchParams, setSearchParams])

  // ---- Local UI state -------------------------------------------------------
  const [searchQuery, setSearchQuery] = useState("")
  const [searchCategory, setSearchCategory] = useState<string | null>(null)
  const [searchMode, setSearchMode] = useState<SearchMode>("ai")
  const [builderSlides, setBuilderSlides] = useState<BuilderSlide[]>([])
  // Builder state is React-only; sessionStorage draft scope + seen flags
  // survive reload. Rotate once on mount so an empty post-reload draft does
  // not inherit a stale "seen" nudge from the wiped previous draft.
  useEffect(() => {
    rotateDraftPreviewHintScopeId()
  }, [])
  // Which presenter modes the tenant admin allows (#2336 H). Declared up here
  // because presentation handlers below gate on it. Defaults to both enabled.
  const presenterModes = usePresenterModes()
  const [previewSlide, setPreviewSlide] = useState<SlideSenseSlide | null>(null)
  // Navigation context for the preview modal — the slide list to flip
  // through via prev/next. In browse (decks-first) mode this is the
  // clicked slide's deck; in search mode it's the flat result set.
  const [previewContext, setPreviewContext] = useState<SlideSenseSlide[]>([])
  const [activeSlide, setActiveSlide] = useState<SlideSenseSlide | null>(null)
  const [overItemId, setOverItemId] = useState<string | null>(null)
  const [isPresenting, setIsPresenting] = useState(false)
  // When non-null, a presenter window is coupled to this presentation
  // via BroadcastChannel. `null` = single-window flow.
  const [presentationSessionId, setPresentationSessionId] = useState<string | null>(null)
  // Shown when the browser blocks the presenter-view popup. We don't
  // enter the overlay in that case (a dialog inside fullscreen would be
  // invisible, #2127) — instead this persistent dialog offers Retry /
  // "present without presenter view".
  const [popupBlockedOpen, setPopupBlockedOpen] = useState(false)
  // Handle to the presenter-view popup window (the `window.open` result),
  // so exiting the presentation can close it — leaving it orphaned is
  // confusing (#2153). Stays null on the single-window flow.
  const presenterWindowRef = useRef<Window | null>(null)

  // Portal host for the presentation overlay. Created lazily once and
  // attached to `document.body` so the overlay escapes any parent layout
  // (flex containers, transform ancestors, iframes, etc.) and covers the
  // entire viewport regardless of where this page is mounted. Keeping a
  // stable DOM node across mounts also lets us call
  // `portalEl.requestFullscreen()` synchronously inside a click handler —
  // Chrome/Safari reject fullscreen requests that happen after the click
  // task has ended (e.g., from a useLayoutEffect on a freshly mounted
  // component), so the portal host must already exist when the user
  // clicks "Presentera".
  const portalHostRef = useRef<HTMLDivElement | null>(null)
  if (portalHostRef.current === null && typeof document !== "undefined") {
    const el = document.createElement("div")
    el.setAttribute("data-slide-sense-presentation-root", "")
    el.style.position = "fixed"
    el.style.inset = "0"
    el.style.zIndex = "9999"
    el.style.display = "none"
    portalHostRef.current = el
  }

  // Attach/detach the portal host to the document body. We keep it around
  // for the lifetime of the page so fullscreen can latch onto a stable
  // element; display toggling is done via `isPresenting` below.
  useEffect(() => {
    const el = portalHostRef.current
    if (!el || typeof document === "undefined") return
    document.body.appendChild(el)
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el)
    }
  }, [])

  // Show / hide the portal host as the presentation toggles. Using a
  // direct style mutation (rather than conditional rendering of the host
  // itself) keeps the element stable so fullscreen and React portaling
  // target the same node across toggles.
  useEffect(() => {
    const el = portalHostRef.current
    if (!el) return
    el.style.display = isPresenting ? "block" : "none"
  }, [isPresenting])

  // Global "/" shortcut to focus the visible search input. Skip when the
  // user is typing in another input/textarea/contenteditable, in a Radix
  // dialog (would interfere with form fields), or while presenting.
  useEffect(() => {
    if (isPresenting) return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      ) {
        return
      }
      const input = document.querySelector<HTMLInputElement>("[data-search-input]:not([disabled])")
      if (input) {
        e.preventDefault()
        input.focus()
        input.select()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [isPresenting])

  // Enter native fullscreen synchronously inside the click handler. The
  // browser's user-activation check requires `requestFullscreen()` to run
  // in the same task as the click — any Promise microtask, React commit
  // phase, or `useLayoutEffect` from a newly-mounted component is too
  // late on Chrome/Safari. We call it on the portal host so the
  // fullscreened element is our full-viewport overlay, not the whole
  // document.
  //
  // If the request is rejected (e.g., iframe without `allow="fullscreen"`)
  // we silently fall through — the portal host already covers the full
  // viewport at z-9999, so the presentation still works. No toast, no
  // noise.
  const requestPresentationFullscreen = useCallback(() => {
    const el = portalHostRef.current
    if (!el || typeof el.requestFullscreen !== "function") return
    if (typeof document !== "undefined" && document.fullscreenElement === el) return
    try {
      // `navigationUI: "hide"` asks the browser to hide all chrome (URL bar,
      // tabs, status), giving the same edge-to-edge result as F11. Browsers
      // that don't support the option ignore it.
      void el.requestFullscreen({ navigationUI: "hide" }).catch(() => {
        // Silent fallback — overlay still covers the viewport.
      })
    } catch {
      // Silent fallback — overlay still covers the viewport.
    }
  }, [])

  // Collections state (My Slides) — declared here (ahead of its usual block
  // below) so handleStartPresenting/handleStartPresentingWithPresenterView
  // can depend on it without a temporal-dead-zone error.
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null)

  const handleStartPresenting = useCallback(() => {
    if (builderSlides.length === 0) return
    // Defense in depth: audience Presentation Mode may be disabled by the
    // tenant admin (#2336 H). The Present button is hidden in that case, but
    // the presenter-view popup-blocked fallback could otherwise route here —
    // never enter a disabled mode.
    if (!presenterModes.presentation) return
    telemetry.deckPresented(
      builderSlides.map((bs) => bs.slide.id),
      "presentation",
      activeCollectionId
    )
    // requestFullscreen MUST run synchronously in the click task to
    // preserve the user-gesture token. Do this before any setState
    // (setState is async and would push the call off the current task).
    requestPresentationFullscreen()
    setPresentationSessionId(null)
    setIsPresenting(true)
  }, [
    builderSlides,
    presenterModes.presentation,
    requestPresentationFullscreen,
    telemetry,
    activeCollectionId,
  ])

  // "Presentera med presenter view" — open the presenter popup FIRST, and
  // only request fullscreen once it actually opened.
  //
  // Ordering matters for the blocked case: if we went fullscreen up front
  // and the popup blocker then denied the open, the audience window would
  // be stuck in fullscreen with the "allow popups" dialog stranded outside
  // the fullscreen element (invisible / unclickable, #2127) AND the browser
  // URL bar hidden — so the user couldn't even allow popups. By opening
  // first we stay in normal layout when blocked: the dialog is clickable
  // and the URL bar's popup control is reachable (#2153).
  //
  // window.open and requestFullscreen both need transient activation;
  // Chromium only consumes it at the end of the click task, so requesting
  // fullscreen right after a synchronous window.open in the same handler
  // still counts as user-activated.
  //
  // Opening the popup STEALS OS focus from the audience window, which can
  // make Chrome abort the just-requested fullscreen — so we hand focus
  // straight back with blur()/focus(). Even if that fails, PresentationMode
  // never tears the presentation down on a fullscreen-exit on this
  // dual-window path (#2235) — the overlay covers the viewport via
  // display:block even if native fullscreen never latches.
  const handleStartPresentingWithPresenterView = useCallback(() => {
    if (builderSlides.length === 0) return
    const sessionId = (() => {
      if (typeof crypto !== "undefined") {
        if (typeof crypto.randomUUID === "function") return crypto.randomUUID()
        if (typeof crypto.getRandomValues === "function") {
          const bytes = new Uint8Array(16)
          crypto.getRandomValues(bytes)
          bytes[6] = (bytes[6] & 0x0f) | 0x40
          bytes[8] = (bytes[8] & 0x3f) | 0x80
          const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"))
          return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`
        }
      }
      return `${Date.now()}-${performance.now().toString(36).replace(".", "")}`
    })()
    let presenterWindow: Window | null = null
    try {
      presenterWindow = window.open(
        `/slide-sense/present?session=${encodeURIComponent(sessionId)}`,
        "slide-sense-presenter",
        "popup=yes,width=1280,height=720"
      )
    } catch {
      presenterWindow = null
    }
    if (!presenterWindow) {
      // Popup blocked — do NOT go fullscreen, so the dialog is clickable
      // and the user can reach the browser's popup control to allow it.
      setPopupBlockedOpen(true)
      return
    }
    // Popup opened — now go fullscreen (still within the user-gesture task),
    // then hand focus back to the audience window so the popup stealing
    // focus doesn't abort the fullscreen (#2153). Both calls are
    // best-effort; browsers that disallow them just ignore them.
    telemetry.deckPresented(
      builderSlides.map((bs) => bs.slide.id),
      "presenter_view",
      activeCollectionId
    )
    requestPresentationFullscreen()
    try {
      presenterWindow.blur()
    } catch {
      // Ignore.
    }
    try {
      window.focus()
    } catch {
      // Ignore.
    }
    // Keep a handle so exiting the presentation can close this window.
    presenterWindowRef.current = presenterWindow
    setPresentationSessionId(sessionId)
    setIsPresenting(true)
  }, [builderSlides, requestPresentationFullscreen, telemetry, activeCollectionId])

  const handleExitPresenting = useCallback(() => {
    // Close the coupled presenter-view popup so it doesn't linger after the
    // presentation ends (#2153). No-op for the single-window flow.
    const presenterWindow = presenterWindowRef.current
    presenterWindowRef.current = null
    if (presenterWindow) {
      try {
        presenterWindow.close()
      } catch {
        // Ignore — window may already be closed by the user.
      }
    }
    setIsPresenting(false)
    setPresentationSessionId(null)
  }, [])

  // Collections state (My Slides)
  const [loadingCollectionId, setLoadingCollectionId] = useState<string | null>(null)
  const [downloadingCollectionId, setDownloadingCollectionId] = useState<string | null>(null)
  const [activeCollectionName, setActiveCollectionName] = useState<string | null>(null)
  // Snapshot of slide IDs when the active collection was last loaded or saved.
  // Compared to builder slide IDs to detect unsaved edits (#82 divergence).
  const [savedSlideIds, setSavedSlideIds] = useState<string[]>([])
  const [removedSlides, setRemovedSlides] = useState<RemovedSlide[]>([])

  // Mobile builder drawer state — aside is hidden below md breakpoint, replaced by Sheet
  const [mobileBuilderOpen, setMobileBuilderOpen] = useState(false)
  // Below the `md` breakpoint we drop the resizable Builder panel entirely
  // and show the mobile FAB + Sheet drawer instead. The breakpoint matches
  // Tailwind's `md` so the FAB/Sheet markup keeps working as-is.
  const isMobile = useViewportBelow({ breakpoint: 768 })

  // Guard against tab close / reload when builder has unsaved edits.
  // Dirty = either an unsaved new collection (no activeCollectionId) or
  // active collection diverged from saved snapshot.
  useEffect(() => {
    const isDirty =
      builderSlides.length > 0 &&
      (activeCollectionId === null ||
        builderSlides.length !== savedSlideIds.length ||
        builderSlides.some((bs, i) => bs.slide.id !== savedSlideIds[i]))
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [builderSlides, activeCollectionId, savedSlideIds])

  // Admin state
  const [adminDecks, setAdminDecks] = useState<SlideSenseDeck[]>([])
  const [adminFolders, setAdminFolders] = useState<SlideSenseFolder[]>([])
  const [isAdminLoading, setIsAdminLoading] = useState(false)
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [adminSearchMode, setAdminSearchMode] = useState<SearchMode>("ai")
  const [adminSearchQuery, setAdminSearchQuery] = useState("")
  const [adminSearchCategory, setAdminSearchCategory] = useState<string | null>(null)
  const [adminShowSearch, setAdminShowSearch] = useState(false)

  // ---- Data fetching -------------------------------------------------------
  const { data: folders = [], refetch: refetchFolders } = useFolders()
  // Browse mode no longer fetches all slides for the folder up front —
  // DeckBrowser fetches decks first, and each DeckSection pulls its own
  // slides on expand. `browseSlides` is kept as an empty fallback so the
  // display-mode switch below stays simple.
  const slidesLoading = false
  const browseSlides: SlideSenseSlide[] = []

  // Search scope is a union of explicitly-added folder/collection chips and
  // the implicit scope (the folder the user is currently navigated into).
  // When chips are set, they win; otherwise the implicit folder is used so
  // the current "in folder X" behaviour stays the default.
  const [searchScopeFolderIds, setSearchScopeFolderIds] = useState<string[]>([])
  const [searchScopeCollectionIds, setSearchScopeCollectionIds] = useState<string[]>([])

  // When the user navigates to a different folder via the sidebar, clear any
  // explicit chips so the chip strip re-derives from the new folder. Without
  // this the chips would stay stuck on the previously-selected folder.
  useEffect(() => {
    setSearchScopeFolderIds([])
    setSearchScopeCollectionIds([])
  }, [selectedFolderId, showMyPresentations])

  const effectiveSearchFolderIds = useMemo<string[]>(() => {
    if (searchScopeFolderIds.length > 0) return searchScopeFolderIds
    return selectedFolderId ? [selectedFolderId] : []
  }, [searchScopeFolderIds, selectedFolderId])

  const { data: searchResults, isLoading: searchLoading } = useSlideSearch(searchQuery, {
    category: searchCategory ?? undefined,
    folderIds: effectiveSearchFolderIds,
    collectionIds: searchScopeCollectionIds,
  })

  // True when the user has narrowed the scope beyond the implicit "current
  // folder" view. Drives the browse-mode swap below: with explicit chips
  // set, browsing decks-first would silently show items outside the scope —
  // so we flatten to a single in-scope slide grid instead.
  const hasExplicitScope = searchScopeFolderIds.length > 0 || searchScopeCollectionIds.length > 0

  const {
    data: scopedSlidesData,
    isLoading: scopedSlidesLoading,
    fetchNextPage: fetchNextScopedPage,
    hasNextPage: hasNextScopedPage,
    isFetchingNextPage: isFetchingNextScopedPage,
  } = useSlides({
    folderIds: hasExplicitScope ? effectiveSearchFolderIds : undefined,
    collectionIds: hasExplicitScope ? searchScopeCollectionIds : undefined,
    enabled: hasExplicitScope,
  })

  const scopedSlides = useMemo<SlideSenseSlide[]>(
    () => scopedSlidesData?.pages.flat() ?? [],
    [scopedSlidesData]
  )

  // Collections data (My Slides)
  const {
    data: collections = [],
    isLoading: collectionsLoading,
    refetch: refetchCollections,
  } = useCollections()

  // Sync collection count to nav store so sidebar badge updates
  useEffect(() => {
    setNavCollectionCount(collections.length)
  }, [collections.length, setNavCollectionCount])

  // Refetch collections when My Slides view is activated
  useEffect(() => {
    if (showMyPresentations) void refetchCollections()
  }, [showMyPresentations, refetchCollections])

  // AI search (library + admin each get their own instance). The library
  // instance picks up the same chip-strip scope as keyword search; admin
  // stays unscoped (admin search runs across all slides by design).
  const aiSearch = useAiSearch({
    folderIds: effectiveSearchFolderIds,
    collectionIds: searchScopeCollectionIds,
  })
  const adminAiSearch = useAiSearch()

  // Admin keyword search
  const { data: adminSearchResults, isLoading: adminSearchLoading } = useSlideSearch(
    adminSearchMode === "keyword" ? adminSearchQuery : "",
    { category: adminSearchCategory ?? undefined }
  )
  const isAdminSearchActive =
    (adminSearchMode === "keyword" && adminSearchQuery.length >= 2) ||
    (adminSearchMode === "ai" && adminAiSearch.results.length > 0)
  const adminDisplaySlides =
    adminSearchMode === "ai" && adminAiSearch.results.length > 0
      ? adminAiSearch.results
      : (adminSearchResults ?? [])

  // Determine which slides to display based on active mode
  const isKeywordSearchActive = searchMode === "keyword" && searchQuery.length >= 2
  // A completed AI search — true even when it returned zero results, so the
  // page shows a "no results" empty state instead of silently falling back to
  // browse mode. `isAiSearchActive` stays results-gated and only drives the
  // preview-navigation context below (which needs actual result slides).
  const isAiSearchPerformed = searchMode === "ai" && aiSearch.hasSearched
  const isAiSearchActive = searchMode === "ai" && aiSearch.results.length > 0
  // Any search whose results (possibly empty) should own the results pane.
  const isSearchResultsView = isKeywordSearchActive || isAiSearchPerformed

  let displaySlides: typeof browseSlides
  if (isAiSearchPerformed) displaySlides = aiSearch.results
  else if (isKeywordSearchActive) displaySlides = searchResults ?? []
  else displaySlides = browseSlides

  let isDisplayLoading: boolean
  if (isAiSearchPerformed) isDisplayLoading = false
  else if (isKeywordSearchActive) isDisplayLoading = searchLoading
  else isDisplayLoading = slidesLoading

  // For the preview modal we want the slides in the same order they're rendered
  // in the library. Search modes use the flat result set; browse mode uses
  // whatever the DeckSection passed up via onPreview (its own deck's slides).
  const previewNavSlides = useMemo(() => {
    if (isKeywordSearchActive || isAiSearchActive) return displaySlides
    return previewContext
  }, [displaySlides, isKeywordSearchActive, isAiSearchActive, previewContext])

  // ---- Admin data fetching -------------------------------------------------
  const adminLoadedOnce = useRef(false)
  const adminInflight = useRef(false)
  const adminFailedAt = useRef<number>(0)

  const loadAdminData = useCallback(async () => {
    // Coalesce concurrent calls and back off after a failure to prevent
    // re-render storms from turning one transient error into a request flood.
    if (adminInflight.current) return
    if (adminFailedAt.current && Date.now() - adminFailedAt.current < 30_000) return
    adminInflight.current = true
    if (!adminLoadedOnce.current) setIsAdminLoading(true)
    try {
      const [decks, adminFlds, me] = await Promise.all([
        fetchAdminDecks(),
        fetchAdminFolders(),
        fetchSlideSenseAdminMe().catch(() => ({ is_admin: true, is_super_admin: false })),
      ])
      setAdminDecks(Array.isArray(decks) ? decks : [])
      setAdminFolders(Array.isArray(adminFlds) ? adminFlds : [])
      setIsAdmin(true)
      setIsSuperAdmin(Boolean(me.is_super_admin))
      setNavIsAdmin(true)
      adminLoadedOnce.current = true
      adminFailedAt.current = 0
    } catch (err) {
      // Only treat 401/403 as "not admin" — other errors are transient
      const status = (err as { response?: { status?: number } }).response?.status
      if (status === 401 || status === 403) {
        setIsAdmin(false)
        setNavIsAdmin(false)
      } else {
        adminFailedAt.current = Date.now()
        console.error("Failed to load admin data:", err)
        toast.error(t("slideSense.failedToLoadAdmin"))
      }
    } finally {
      adminInflight.current = false
      setIsAdminLoading(false)
    }
  }, [setNavIsAdmin, t])

  // Load admin data when switching to admin tab (driven by sidebar)
  useEffect(() => {
    if (activeTab === "admin") void loadAdminData()
  }, [activeTab, loadAdminData])

  // ---- Collection handlers (My Slides) ------------------------------------
  const handleLoadCollection = useCallback(
    async (collection: SlideSenseCollection) => {
      setLoadingCollectionId(collection.id)
      try {
        const full = await fetchCollection(collection.id)
        if (full.slides && full.slides.length > 0) {
          const newBuilderSlides: BuilderSlide[] = full.slides.map((slide) => ({
            id: uuidv4(),
            slide,
          }))
          setBuilderSlides(newBuilderSlides)
          setActiveCollectionId(collection.id)
          setActiveCollectionName(collection.name)
          setSavedSlideIds(full.slides.map((s) => s.id))
          setRemovedSlides(full.removed_slides ?? [])
          // Stay on the Mina presentationer view — the slides flow into the
          // Builder panel, the main view does not navigate away.
          toast.success(t("slideSense.loadedCollection", { name: collection.name }))
        } else {
          toast.error(t("slideSense.collectionEmpty"))
        }
      } catch (err) {
        console.error("Operation failed:", err)
        toast.error(t("slideSense.failedToLoadCollection"))
      } finally {
        setLoadingCollectionId(null)
      }
    },
    [t]
  )

  const handleDownloadCollection = useCallback(
    async (collection: SlideSenseCollection) => {
      setDownloadingCollectionId(collection.id)
      try {
        // Send the same normalized filename the Builder does (trimmed, single
        // .pptx) rather than the raw collection name — the two download paths
        // should be indistinguishable to the API. The backend is what makes the
        // name header-safe (#2591); this just keeps the paths consistent.
        const safeName = collection.name.trim().replace(/\.pptx$/i, "") || "presentation"
        const filename = `${safeName}.pptx`
        const blob = await mergeSlides(collection.slide_ids, filename)
        downloadBlob(blob, filename)
        toast.success(t("slideSense.downloading", { name: collection.name }))
      } catch (err) {
        console.error("Operation failed:", err)
        toast.error(t("slideSense.failedToDownload"))
      } finally {
        setDownloadingCollectionId(null)
      }
    },
    [t]
  )

  // Collection delete confirmation. Uses an AlertDialog (rendered below)
  // instead of `window.confirm` so it matches the rest of the app's chrome
  // (Radix-based, themed, screen-reader friendly).
  const [deleteCollectionTarget, setDeleteCollectionTarget] = useState<SlideSenseCollection | null>(
    null
  )
  const [deleteCollectionBusy, setDeleteCollectionBusy] = useState(false)

  const handleDeleteCollection = useCallback((collection: SlideSenseCollection) => {
    setDeleteCollectionTarget(collection)
  }, [])

  const confirmDeleteCollection = useCallback(async () => {
    if (!deleteCollectionTarget) return
    const collection = deleteCollectionTarget
    setDeleteCollectionBusy(true)
    try {
      await deleteCollection(collection.id)
      toast.success(t("slideSense.collectionDeleted", { name: collection.name }))
      setDeleteCollectionTarget(null)
      void refetchCollections()
    } catch (err) {
      console.error("Operation failed:", err)
      toast.error(t("slideSense.failedToDeleteCollection"))
    } finally {
      setDeleteCollectionBusy(false)
    }
  }, [deleteCollectionTarget, refetchCollections, t])

  // Check admin status on mount and preload admin data if admin
  React.useEffect(() => {
    if (isAdmin === null) {
      // Fire a lightweight check -- try fetching admin folders
      fetchAdminFolders()
        .then(() => {
          setIsAdmin(true)
          // Preload admin data so it's ready when the tab is opened
          void loadAdminData()
        })
        .catch(() => setIsAdmin(false))
    }
  }, [isAdmin, loadAdminData])

  // ---- DnD setup -----------------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor)
  )

  // Pin the drag overlay so the cursor sits at its center, regardless of
  // where on the source card the user grabbed.  Without this the overlay
  // is a different size than the source card and the slide visually drifts
  // away from the cursor as the user moves.
  const snapCenterToCursor: Modifier = ({ activatorEvent, draggingNodeRect, transform }) => {
    if (!draggingNodeRect || !activatorEvent) return transform
    const ev = activatorEvent as PointerEvent
    const offsetX = ev.clientX - draggingNodeRect.left
    const offsetY = ev.clientY - draggingNodeRect.top
    return {
      ...transform,
      x: transform.x + offsetX - draggingNodeRect.width / 2,
      y: transform.y + offsetY - draggingNodeRect.height / 2,
    }
  }

  // ---- Builder operations --------------------------------------------------
  const addSlideToBuilder = useCallback(
    (slide: SlideSenseSlide, source: SlideAddSource = "library") => {
      telemetry.slideAddedToBuilder(slide.id, source)
      setBuilderSlides((prev) => [...prev, { id: uuidv4(), slide }])
    },
    [telemetry]
  )

  const addDeckToBuilder = useCallback(
    (deckSlides: SlideSenseSlide[]) => {
      const renderable = deckSlides.filter((s) => s.processing_state !== "render_failed")
      if (renderable.length === 0) return
      telemetry.slidesAddedToBuilderBatch(
        renderable.map((s) => s.id),
        "library-deck"
      )
      setBuilderSlides((prev) => [...prev, ...renderable.map((slide) => ({ id: uuidv4(), slide }))])
      toast.success(t("slideSense.addedDeckToBuilder", { count: renderable.length }))
    },
    [telemetry, t]
  )

  const removeFromBuilder = useCallback((builderId: string) => {
    setBuilderSlides((prev) => prev.filter((s) => s.id !== builderId))
  }, [])

  const clearBuilder = useCallback(() => {
    setBuilderSlides([])
    setActiveCollectionId(null)
    setActiveCollectionName(null)
    setSavedSlideIds([])
    setRemovedSlides([])
  }, [])

  // When the landing's "Ny presentation" CTA fires we mirror the builder's
  // own confirm-then-clear flow so users with unsaved work in the builder
  // get the same safeguard. The builder dialog lives inside SlideBuilder
  // and is unreachable from the home view, so we render a parallel one
  // here driven by ``confirmStartNewOpen``. (#2)
  const [confirmStartNewOpen, setConfirmStartNewOpen] = useState(false)
  const startNewFromLanding = useCallback(() => {
    if (builderSlides.length > 0) {
      setConfirmStartNewOpen(true)
      return
    }
    rotateDraftPreviewHintScopeId()
    clearBuilder()
    // Customer routing — "New presentation" lands in My presentations
    // (not All Slides) so the user picks a starting point or names a
    // new collection there. Search/Browse keep going to All Slides.
    nav.selectMyPresentations()
  }, [builderSlides.length, clearBuilder, nav])

  const handleAcknowledgeRemovals = useCallback(async () => {
    if (!activeCollectionId) return
    setRemovedSlides([])
    try {
      await acknowledgeRemovals(activeCollectionId)
      void refetchCollections()
    } catch (err) {
      console.error("Operation failed:", err)
    }
  }, [activeCollectionId, refetchCollections])

  // Stable refs for callbacks passed to dual SlideBuilder instances
  // (desktop aside + mobile Sheet). Inlining them recreated the function
  // identity each render, which forced both SlideBuilder trees to
  // re-render on every parent state change.
  const builderSlidesRef = useRef(builderSlides)
  builderSlidesRef.current = builderSlides

  const handleCollectionSaved = useCallback((id: string, name: string) => {
    setActiveCollectionId(id)
    setActiveCollectionName(name)
    setSavedSlideIds(builderSlidesRef.current.map((bs) => bs.slide.id))
  }, [])

  const handleRemoveSlidesFromBuilder = useCallback((slideIds: string[]) => {
    const ids = new Set(slideIds)
    setBuilderSlides((prev) => prev.filter((bs) => !ids.has(bs.slide.id)))
  }, [])

  // ---- DnD handlers --------------------------------------------------------
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as { type?: string; slide?: SlideSenseSlide } | undefined
    if (data?.type === "slide-sense-slide" && data.slide) {
      setActiveSlide(data.slide)
    }
  }, [])

  const handleDragOver = useCallback((event: { over: { id: string | number } | null }) => {
    setOverItemId(event.over?.id != null ? String(event.over.id) : null)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveSlide(null)
      setOverItemId(null)

      const { active, over } = event

      // Drag-out-to-remove: if a builder slide is dropped outside any droppable, remove it
      if (!over) {
        const isBuilderSlide = builderSlides.some((s) => s.id === active.id)
        if (isBuilderSlide) {
          removeFromBuilder(active.id as string)
        }
        return
      }

      // Check if a library card was dropped onto the builder zone or a builder item
      const activeData = active.data.current as
        | { type?: string; slide?: SlideSenseSlide }
        | undefined

      if (activeData?.type === "slide-sense-slide" && activeData.slide) {
        // Only add to builder if dropped on the builder zone, the
        // dedicated trailing-zone, or a specific builder item.
        const isBuilderTarget =
          over.id === "slide-builder-drop-zone" ||
          over.id === "slide-builder-trail" ||
          builderSlides.some((s) => s.id === over.id)

        if (!isBuilderTarget) {
          // Dropped back on library or elsewhere — do nothing
          setOverItemId(null)
          return
        }

        if (over.id === "slide-builder-drop-zone" || over.id === "slide-builder-trail") {
          // Trail = explicit "append at end" droppable. Wrapper zone falls
          // through to addSlideToBuilder which also pushes to the end, so
          // both routes match user expectation. (drop-zone vs trail differ
          // only in which placeholder lit up while dragging.)
          addSlideToBuilder(activeData.slide, "dragdrop")
        } else {
          setBuilderSlides((prev) => {
            const overIndex = prev.findIndex((s) => s.id === over.id)
            if (overIndex === -1) {
              return [...prev, { id: uuidv4(), slide: activeData.slide! }]
            }
            const newItem: BuilderSlide = { id: uuidv4(), slide: activeData.slide! }
            const next = [...prev]
            next.splice(overIndex, 0, newItem)
            return next
          })
        }
        setOverItemId(null)
        return
      }

      // Reorder within builder
      if (active.id !== over.id) {
        setBuilderSlides((prev) => {
          const oldIndex = prev.findIndex((s) => s.id === active.id)
          const newIndex = prev.findIndex((s) => s.id === over.id)
          if (oldIndex === -1 || newIndex === -1) return prev
          return arrayMove(prev, oldIndex, newIndex)
        })
      }
    },
    [addSlideToBuilder, builderSlides, removeFromBuilder]
  )

  // ---- Render --------------------------------------------------------------
  return (
    <SidebarInset className="relative flex h-[calc(100svh-64px)] min-h-[calc(100svh-64px)] w-full flex-col overflow-hidden">
      {/* Home tab — welcome dashboard with quick actions, recently uploaded
          decks and recently edited presentations. Clicking any action jumps
          into the existing 3-panel library. (#5) */}
      {activeTab === "home" && (
        <SlideHubLanding
          onStartNewPresentation={startNewFromLanding}
          onOpenCollection={(collectionId) => {
            const c = collections.find((x) => x.id === collectionId)
            if (c) {
              nav.selectMyPresentations()
              void handleLoadCollection(c)
            }
          }}
          onOpenDeck={(_deckId, folderId) => {
            if (folderId) nav.selectFolder(folderId)
            else nav.selectAllSlides()
          }}
        />
      )}

      {/* Library tab */}
      {activeTab === "library" && (
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[snapCenterToCursor]}
          sensors={sensors}
          autoScroll={{
            enabled: true,
            layoutShiftCompensation: false,
            threshold: { x: 0, y: 0.2 },
          }}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragStart={handleDragStart}
        >
          <div className="mx-auto w-full max-w-[1800px] flex-1 overflow-hidden">
            <ResizablePanelGroup
              autoSave="slide-sense:builder-split:v2"
              className="h-full"
              direction="horizontal"
            >
              {/* Center: search bar + slide grid */}
              <ResizablePanel defaultSize="72%" id="slide-sense-library" minSize="40%">
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                  {/* Section header */}
                  <header className="flex h-14 shrink-0 items-center gap-3 border-b px-5">
                    {showMyPresentations ? (
                      <>
                        <BookOpen className="text-primary h-5 w-5" />
                        <h1 className="text-2xl font-semibold tracking-tight">
                          {t("slideSense.myPresentations")}
                        </h1>
                        {collections.length > 0 && (
                          <span className="bg-muted text-muted-foreground rounded-md px-2 py-0.5 text-xs tabular-nums">
                            {collections.length}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <LayoutGrid className="text-muted-foreground h-5 w-5" />
                        <h1 className="text-2xl font-semibold tracking-tight">
                          {selectedFolderId
                            ? (folders.find((f) => f.id === selectedFolderId)?.name ??
                              t("slideSense.slides"))
                            : t("slideSense.allSlides")}
                        </h1>
                      </>
                    )}
                  </header>

                  {showMyPresentations ? (
                    /* My Slides view */
                    <div className="flex-1 overflow-y-auto p-4">
                      <div>
                        <MyPresentationsList
                          collections={collections}
                          downloadingCollectionId={downloadingCollectionId}
                          isLoading={collectionsLoading}
                          loadingCollectionId={loadingCollectionId}
                          onDelete={handleDeleteCollection}
                          onDownload={handleDownloadCollection}
                          onLoad={handleLoadCollection}
                        />
                      </div>
                    </div>
                  ) : (
                    /* Library view */
                    <>
                      {/* Breadcrumbs */}
                      <div className="shrink-0 border-b px-4 py-2">
                        <FolderBreadcrumbs
                          folders={folders}
                          selectedFolderId={selectedFolderId}
                          onSelectFolder={(id) => nav.selectFolder(id)}
                        />
                      </div>

                      {/* Search bar */}
                      <div className="shrink-0 border-b px-4 py-3">
                        <SlideSearchBar
                          category={searchCategory}
                          hasAiResults={aiSearch.results.length > 0}
                          hasAiSearched={aiSearch.hasSearched}
                          isAiSearching={aiSearch.isSearching}
                          mode={searchMode}
                          query={searchQuery}
                          onAiClear={aiSearch.clear}
                          onAiSearch={aiSearch.search}
                          onCategoryChange={setSearchCategory}
                          onQueryChange={setSearchQuery}
                          onModeChange={(m) => {
                            setSearchMode(m)
                            if (m === "keyword") aiSearch.clear()
                          }}
                        />
                        {/* Scope chip strip — shows the active search scope
                            (folders + saved presentations) and lets the user
                            add / remove chips. Implicit folder scope from the
                            sidebar appears as a non-removable chip until the
                            user explicitly adds chips of their own. */}
                        <div className="mt-2">
                          <SearchScopeChips
                            collectionIds={searchScopeCollectionIds}
                            collections={collections}
                            folderIds={searchScopeFolderIds}
                            folders={folders}
                            implicitFolderId={selectedFolderId}
                            onChange={(next) => {
                              setSearchScopeFolderIds(next.folderIds)
                              setSearchScopeCollectionIds(next.collectionIds)
                            }}
                          />
                        </div>
                        {/* AI search summary */}
                        {isAiSearchPerformed && aiSearch.summary && (
                          <div className="bg-primary/5 mt-2 flex items-start gap-2 rounded-md px-3 py-2">
                            <Sparkles className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                            <p className="text-foreground/80 text-sm">{aiSearch.summary}</p>
                          </div>
                        )}
                        {aiSearch.error && (
                          <div className="bg-destructive/10 mt-2 flex items-center gap-2 rounded-md px-3 py-2">
                            <AlertCircle className="text-destructive h-4 w-4 shrink-0" />
                            <p className="text-destructive text-sm">{aiSearch.error}</p>
                          </div>
                        )}
                      </div>

                      {/* Slide library grid */}
                      <div className="flex-1 overflow-y-auto p-4">
                        {/* AI searching indicator */}
                        {aiSearch.isSearching && (
                          <div className="flex items-center justify-center gap-2 py-8">
                            <Loader2 className="text-primary h-5 w-5 animate-spin" />
                            <span className="text-muted-foreground text-sm">
                              {t("slideSense.aiSearching")}
                            </span>
                          </div>
                        )}
                        {!aiSearch.isSearching && isSearchResultsView && (
                          // Search results: flat grid through the legacy
                          // SlideLibrary. Grouping would be noise here since
                          // matches usually span decks. `searchActive` makes the
                          // empty state read "no results" rather than "no slides".
                          <SlideLibrary
                            searchActive
                            folders={folders}
                            groupByDeck={false}
                            isLoading={isDisplayLoading}
                            slides={displaySlides}
                            onAdd={(slide) => addSlideToBuilder(slide, "library")}
                            onPreview={(slide) => {
                              setPreviewContext(displaySlides)
                              setPreviewSlide(slide)
                            }}
                          />
                        )}
                        {!aiSearch.isSearching && !isSearchResultsView && hasExplicitScope && (
                          // Explicit scope chips set — flatten to a slide
                          // grid filtered server-side by the scope. Browsing
                          // by deck here would silently show items from
                          // outside the chosen folders / presentations.
                          <SlideLibrary
                            folders={folders}
                            groupByDeck={false}
                            hasNextPage={hasNextScopedPage}
                            isFetchingNextPage={isFetchingNextScopedPage}
                            isLoading={scopedSlidesLoading}
                            slides={scopedSlides}
                            onAdd={(slide) => addSlideToBuilder(slide, "library")}
                            onFetchNextPage={() => void fetchNextScopedPage()}
                            onPreview={(slide) => {
                              setPreviewContext(scopedSlides)
                              setPreviewSlide(slide)
                            }}
                          />
                        )}
                        {!aiSearch.isSearching && !isSearchResultsView && !hasExplicitScope && (
                          // Browse mode: decks-first. Slides for each deck
                          // load lazily on expand instead of being fetched
                          // up front and grouped client-side.
                          <DeckBrowser
                            folderId={selectedFolderId ?? undefined}
                            folders={folders}
                            onAdd={(slide) => addSlideToBuilder(slide, "library")}
                            onAddDeck={addDeckToBuilder}
                            onPreview={(slide, deckSlides) => {
                              setPreviewContext(deckSlides)
                              setPreviewSlide(slide)
                            }}
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              </ResizablePanel>

              {/* Right panel: Builder. Drops out below the `md` breakpoint —
                  on mobile the FAB + Sheet drawer below takes over. The
                  drag-handle between center and Builder lets the user resize
                  the split; sizes are persisted to localStorage by the
                  panel group's autoSave id. */}
              {!isMobile && (
                <>
                  <ResizableHandle withHandle />
                  <ResizablePanel
                    defaultSize="28%"
                    id="slide-sense-builder"
                    maxSize="50%"
                    minSize="22%"
                  >
                    <aside className="flex h-full min-h-0 flex-col overflow-hidden border-l">
                      <SlideBuilder
                        activeCollectionId={activeCollectionId}
                        activeCollectionName={activeCollectionName}
                        isLibraryDragging={activeSlide !== null}
                        overItemId={overItemId}
                        presenterModes={presenterModes}
                        removedSlides={removedSlides}
                        savedSlideIds={savedSlideIds}
                        slides={builderSlides}
                        onAcknowledgeRemovals={handleAcknowledgeRemovals}
                        onClear={clearBuilder}
                        onCollectionSaved={handleCollectionSaved}
                        onRemove={removeFromBuilder}
                        onRemoveSlidesFromBuilder={handleRemoveSlidesFromBuilder}
                        onStartPresenting={handleStartPresenting}
                        onStartPresentingWithPresenterView={handleStartPresentingWithPresenterView}
                      />
                    </aside>
                  </ResizablePanel>
                </>
              )}
            </ResizablePanelGroup>
          </div>

          {/* Mobile builder FAB — opens Sheet drawer on small viewports */}
          <Button
            aria-label={t("slideSense.builder")}
            className="fixed right-4 bottom-4 z-50 h-12 rounded-full px-4 shadow-lg md:hidden"
            size="sm"
            onClick={() => setMobileBuilderOpen(true)}
          >
            <Layers className="mr-1 h-4 w-4" />
            <span className="font-medium">{t("slideSense.builder")}</span>
            {builderSlides.length > 0 && (
              <Badge
                aria-hidden
                className="bg-primary-foreground text-primary ml-2 px-1.5 py-0 text-xs"
                variant="secondary"
              >
                {builderSlides.length}
              </Badge>
            )}
          </Button>

          {/* Mobile builder Sheet */}
          <Sheet open={mobileBuilderOpen} onOpenChange={setMobileBuilderOpen}>
            <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md" side="right">
              <SheetHeader className="border-b px-4 py-3">
                <SheetTitle>{t("slideSense.builder")}</SheetTitle>
                <SheetDescription className="sr-only">
                  {t("slideSense.dragToBuilder")}
                </SheetDescription>
              </SheetHeader>
              <div className="flex flex-1 flex-col overflow-hidden">
                <SlideBuilder
                  activeCollectionId={activeCollectionId}
                  activeCollectionName={activeCollectionName}
                  isLibraryDragging={activeSlide !== null}
                  overItemId={overItemId}
                  presenterModes={presenterModes}
                  removedSlides={removedSlides}
                  savedSlideIds={savedSlideIds}
                  slides={builderSlides}
                  onAcknowledgeRemovals={handleAcknowledgeRemovals}
                  onClear={clearBuilder}
                  onCollectionSaved={handleCollectionSaved}
                  onRemove={removeFromBuilder}
                  onRemoveSlidesFromBuilder={handleRemoveSlidesFromBuilder}
                  onStartPresenting={handleStartPresenting}
                  onStartPresentingWithPresenterView={handleStartPresentingWithPresenterView}
                />
              </div>
            </SheetContent>
          </Sheet>

          {/* Drag overlay — visual feedback while dragging a library slide */}
          <DragOverlay dropAnimation={null}>
            {activeSlide && (
              <div className="border-primary/40 bg-background w-52 rotate-2 rounded-lg border-2 shadow-2xl">
                <div className="bg-muted/40 aspect-[16/9] w-full overflow-hidden rounded-t-lg">
                  <SlideThumbnailImage
                    slideId={activeSlide.id}
                    thumbnailUrl={activeSlide.thumbnail_url}
                  />
                </div>
                <div className="flex items-center gap-2 p-2">
                  <span className="truncate text-xs font-medium">
                    {activeSlide.ai_title ||
                      `${t("slideSense.slides")} ${activeSlide.slide_index + 1}`}
                  </span>
                  <Badge className="shrink-0 text-[10px]" variant="secondary">
                    {activeSlide.category}
                  </Badge>
                </div>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Admin tab */}
      {activeTab === "admin" && (
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Admin header */}
          <header className="flex shrink-0 items-center gap-3 border-b px-5 py-3">
            <Settings className="text-muted-foreground h-5 w-5" />
            <h1 className="text-2xl font-semibold tracking-tight">{t("slideSense.admin")}</h1>
            {adminDecks.length > 0 && (
              <span className="bg-muted text-muted-foreground rounded-md px-2 py-0.5 text-xs tabular-nums">
                {t("slideSense.folderCount", { count: adminFolders.length })} ·{" "}
                {t("slideSense.deckCount", { count: adminDecks.length })}
              </span>
            )}
          </header>
          <div className="flex flex-1 overflow-hidden">
            {isAdmin === false && (
              <div className="text-muted-foreground flex h-64 w-full flex-col items-center justify-center gap-2">
                <AlertCircle className="text-muted-foreground/40 h-8 w-8" />
                <p className="text-sm">{t("slideSense.noAdminAccess")}</p>
              </div>
            )}
            {isAdmin !== false && isAdminLoading && (
              <div className="flex h-64 w-full items-center justify-center">
                <Spinner className="h-6 w-6" />
              </div>
            )}
            {isAdmin !== false && !isAdminLoading && (
              <>
                {/* File manager */}
                <div
                  className={`flex flex-col overflow-hidden border-r ${adminShowSearch ? "w-1/2" : "flex-1"}`}
                >
                  <AdminFileTree
                    decks={adminDecks}
                    folders={adminFolders}
                    isSuperAdmin={isSuperAdmin}
                    onRefresh={() => {
                      void loadAdminData()
                      void refetchFolders()
                    }}
                  />
                </div>

                {/* Admin search panel */}
                <div
                  className={`flex flex-col overflow-hidden ${adminShowSearch ? "w-1/2" : "w-0"}`}
                >
                  {adminShowSearch && (
                    <>
                      <div className="shrink-0 border-b px-4 py-3">
                        <SlideSearchBar
                          category={adminSearchCategory}
                          hasAiResults={adminAiSearch.results.length > 0}
                          hasAiSearched={adminAiSearch.hasSearched}
                          isAiSearching={adminAiSearch.isSearching}
                          mode={adminSearchMode}
                          query={adminSearchQuery}
                          onAiClear={adminAiSearch.clear}
                          onAiSearch={adminAiSearch.search}
                          onCategoryChange={setAdminSearchCategory}
                          onQueryChange={setAdminSearchQuery}
                          onModeChange={(m) => {
                            setAdminSearchMode(m)
                            if (m === "keyword") adminAiSearch.clear()
                          }}
                        />
                        {adminAiSearch.summary && adminSearchMode === "ai" && (
                          <div className="bg-primary/5 mt-2 flex items-start gap-2 rounded-md px-3 py-2">
                            <Sparkles className="text-primary mt-0.5 h-4 w-4 shrink-0" />
                            <p className="text-foreground/80 text-sm">{adminAiSearch.summary}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto p-4">
                        {adminAiSearch.isSearching && (
                          <div className="flex items-center justify-center gap-2 py-8">
                            <Loader2 className="text-primary h-5 w-5 animate-spin" />
                            <span className="text-muted-foreground text-sm">
                              {t("slideSense.aiSearching")}
                            </span>
                          </div>
                        )}
                        {!adminAiSearch.isSearching && isAdminSearchActive && (
                          <SlideLibrary
                            isLoading={adminSearchLoading}
                            slides={adminDisplaySlides}
                            onAdd={(slide) => addSlideToBuilder(slide, "library")}
                            onPreview={(slide) => {
                              setPreviewContext(adminDisplaySlides)
                              setPreviewSlide(slide)
                            }}
                          />
                        )}
                        {!adminAiSearch.isSearching && !isAdminSearchActive && (
                          <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-16">
                            <Search className="text-muted-foreground/30 h-8 w-8" />
                            <p className="text-sm">{t("slideSense.searchAcrossDecks")}</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          {/* Toggle search panel button — floating */}
          {isAdmin === true && !isAdminLoading && (
            <button
              title={adminShowSearch ? t("slideSense.closeSearch") : t("slideSense.searchSlides")}
              type="button"
              className={`fixed right-6 bottom-6 z-30 flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-colors ${
                adminShowSearch
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-muted-foreground hover:text-foreground border"
              }`}
              onClick={() => setAdminShowSearch((v) => !v)}
            >
              <Search className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {/* Usage tab */}
      {activeTab === "usage" && <SlideSenseUsageDashboard />}

      {/* Help tab */}
      {activeTab === "help" && <SlideSenseHelp />}

      {/* Slide preview modal */}
      <SlidePreviewModal
        canEdit={isAdmin === true}
        open={previewSlide !== null}
        slide={previewSlide}
        slides={previewNavSlides}
        onAddToBuilder={(slide) => addSlideToBuilder(slide, "preview")}
        onClose={() => setPreviewSlide(null)}
        onNavigate={setPreviewSlide}
      />

      {/* Presentera — PowerPoint F5-equivalent fullscreen overlay. When
          `presentationSessionId` is set we're the "master" side of a
          dual-window session coordinated with the presenter view.
          Rendered via a portal attached to `document.body` so the overlay
          escapes any parent layout and the browser fullscreens exactly
          our full-viewport host element. Fullscreen entry is triggered
          synchronously in the click handler above — this component only
          paints into the already-fullscreened host. */}
      {isPresenting && portalHostRef.current
        ? createPortal(
            <PresentationMode
              sessionId={presentationSessionId}
              slides={builderSlides}
              onExit={handleExitPresenting}
            />,
            portalHostRef.current
          )
        : null}

      {/* Presenter-view popup blocked (#2153). We don't enter the overlay in
          this case — a dialog inside fullscreen would be invisible (#2127) —
          so this persistent dialog explains the block and offers Retry /
          "present without presenter view". Plain Buttons (not
          AlertDialogAction) so we fully control open state across retries. */}
      <AlertDialog open={popupBlockedOpen} onOpenChange={setPopupBlockedOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.present.popupBlockedTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("slideSense.present.popupBlockedBody")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("slideSense.cancel")}</AlertDialogCancel>
            {/* Only offer the audience-only fallback when that mode is
                actually enabled for the tenant (#2336 H) — otherwise this
                would let a presenter-only tenant slip into a disabled mode. */}
            {presenterModes.presentation && (
              <Button
                variant="outline"
                onClick={() => {
                  setPopupBlockedOpen(false)
                  handleStartPresenting()
                }}
              >
                {t("slideSense.present.popupBlockedContinueWithout")}
              </Button>
            )}
            <Button
              onClick={() => {
                // Close first; a fresh click is a new user gesture, so the
                // retry can succeed once popups are allowed. If it's still
                // blocked, the handler re-opens this dialog.
                setPopupBlockedOpen(false)
                handleStartPresentingWithPresenterView()
              }}
            >
              {t("slideSense.present.popupBlockedRetry")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Collection delete confirmation — replaces `window.confirm`. */}
      {/* Start-new confirmation for the landing's "Ny presentation" — mirrors
          the dialog inside the builder so the safeguard is consistent no
          matter where the action is triggered from. (#2) */}
      <AlertDialog open={confirmStartNewOpen} onOpenChange={setConfirmStartNewOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.startNewConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("slideSense.startNewConfirmBody", { count: builderSlides.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("slideSense.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                rotateDraftPreviewHintScopeId()
                clearBuilder()
                nav.selectMyPresentations()
                setConfirmStartNewOpen(false)
              }}
            >
              {t("slideSense.startNewConfirmAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deleteCollectionTarget}
        onOpenChange={(open) => {
          if (!open && !deleteCollectionBusy) setDeleteCollectionTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.deleteCollectionTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteCollectionTarget
                ? t("slideSense.deleteConfirm", { name: deleteCollectionTarget.name })
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteCollectionBusy}>
              {t("slideSense.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCollectionBusy}
              onClick={(e) => {
                e.preventDefault()
                void confirmDeleteCollection()
              }}
            >
              {t("slideSense.deletePermanently")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarInset>
  )
}
